import { User } from '@/utils/auth/auth0'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import Dialog from '@mui/material/Dialog'
import CloseIcon from '@mui/icons-material/Close'
import { useCreate, useGetIdentity, useUpdate } from '@refinedev/core'
import { UUID } from 'crypto'
import { useState } from 'react'

interface UpdateProfilesModalProps {
  funderUuid: UUID
  open: boolean
  onClose: () => void
  opportunityUUIDsToUpdate: UUID[]
}

interface GenerateProfileResponse {
  opportunity_uuid: UUID
  fund_name: string
  profile: string
}

export default function UpdateProfilesModal({
  funderUuid,
  open,
  onClose: handleCloseDialog,
  opportunityUUIDsToUpdate,
}: UpdateProfilesModalProps) {
  const [draftProfiles, setDraftProfiles] = useState<GenerateProfileResponse[]>(
    []
  )
  const [savedProfiles, setSavedProfiles] = useState<UUID[]>([])
  const [discardedProfiles, setDiscardedProfiles] = useState<UUID[]>([])
  const { mutate, isLoading } = useCreate()
  const { mutate: update, isLoading: isUpdating } = useUpdate()

  const user = useGetIdentity().data as User

  async function handleGenerateProfiles() {
    mutate(
      {
        resource: 'opportunities',
        values: {
          opportunity_uuids: opportunityUUIDsToUpdate,
        },
        meta: { generateProfilesUrl: `/generate-profiles/${funderUuid}` },
        errorNotification: (error) => {
          return {
            message: 'Error generating profile',
            description:
              error?.message || 'An error occurred while generating profile',
            type: 'error',
          }
        },
        successNotification: (response) => {
          return {
            message: 'Generated profiles successfully',
            type: 'success',
          }
        },
      },
      {
        onSuccess: (response) => {
          setDraftProfiles(response.data.generated_profiles)
        },
      }
    )
  }

  function handleSubmitProfile(opportunityUuid: UUID) {
    if (!draftProfiles.find((p) => p.opportunity_uuid === opportunityUuid)) {
      return
    }
    update(
      {
        resource: 'opportunities',
        id: opportunityUuid,
        values: {
          profile_text: draftProfiles.find(
            (p) => p.opportunity_uuid === opportunityUuid
          )?.profile,
        },
        meta: {
          updated_by:
            user?.name ||
            `${user?.given_name || ''} ${user?.family_name || ''}`,
        },
        errorNotification: (error) => {
          return {
            message: 'Error saving profile',
            description:
              error?.message || 'An error occurred while saving the profile',
            type: 'error',
          }
        },
      },
      {
        onSuccess: (response) => {
          // close draft profile view for this opportunity
          setSavedProfiles([...savedProfiles, response.data.uuid])
        },
      }
    )
  }

  function handleDiscardDraft(opportunityUuid: UUID) {
    // close draft profile view for this opportunity
    setDiscardedProfiles([...discardedProfiles, opportunityUuid])
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconButton
        onClick={handleCloseDialog}
        sx={{ position: 'absolute', top: 0, right: 0 }}
      >
        <CloseIcon />
      </IconButton>{' '}
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          width: '100%',
        }}
      >
        {draftProfiles.length === 0 ? (
          <>
            <Typography variant="h6">
              Generate Profiles for this Funder's Opportunities?
            </Typography>
            <Typography variant="caption">
              No changes will be saved unless you choose to publish.
            </Typography>
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Button
                variant="contained"
                onClick={handleGenerateProfiles}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? 'Generating Profiles...' : 'Generate Profiles'}
              </Button>
              <Button onClick={handleCloseDialog}>Cancel</Button>
            </Box>
          </>
        ) : (
          <>
            <FormControl
              fullWidth
              margin="normal"
              sx={{ mt: 1, display: 'flex', flexDirection: 'column' }}
            >
              <Typography
                variant="h5"
                sx={{ mb: 2, textAlign: 'center', width: '100%' }}
              >
                Please review the generated profiles
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mb: 4,
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  bgcolor: '#f5f5f5',
                  pt: 1,
                  pb: 2,
                  px: 2,
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Click the Publish button to save the changes.
                </Typography>
                <Typography variant="caption">
                  You can edit the generated profiles here if you need to.{' '}
                  <br />
                  Any currently published profiles will be overwritten by the
                  changes you publish here.
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Click the Discard button if you do not need this generated
                  profile.
                </Typography>
                <Typography variant="caption">
                  This will not affect any currently published profiles. <br />{' '}
                  Please also note: This process does not save any Draft
                  versions of profiles, so any profiles generated here that are
                  not published will be discarded.
                </Typography>
              </Box>

              {draftProfiles.map((profile) => (
                <Box key={profile.opportunity_uuid} sx={{ mb: 6, mt: 3 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: '600',
                      textAlign: 'center',
                      fontSize: 20,
                    }}
                  >
                    {profile.fund_name || 'No fund name provided'}
                  </Typography>
                  {savedProfiles.includes(profile.opportunity_uuid) ? (
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: '500',
                        textAlign: 'center',
                        fontSize: 20,
                        color: 'green',
                      }}
                    >
                      Published!
                    </Typography>
                  ) : discardedProfiles.includes(profile.opportunity_uuid) ? (
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: '500',
                        textAlign: 'center',
                        fontSize: 20,
                      }}
                    >
                      Discarded
                    </Typography>
                  ) : (
                    <>
                      <TextField
                        margin="normal"
                        fullWidth
                        multiline
                        rows={20}
                        InputLabelProps={{ shrink: true }}
                        type="text"
                        label="Generated Profile"
                        name="draft_profile_text"
                        value={profile.profile || ''}
                        onChange={(e) => {
                          setDraftProfiles(
                            draftProfiles.map((updatedProfile) =>
                              updatedProfile.opportunity_uuid ===
                              profile.opportunity_uuid
                                ? { ...updatedProfile, profile: e.target.value }
                                : updatedProfile
                            )
                          )
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          onClick={() => {
                            handleSubmitProfile(profile.opportunity_uuid)
                          }}
                        >
                          Publish
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => {
                            handleDiscardDraft(profile.opportunity_uuid)
                          }}
                        >
                          Discard
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
            </FormControl>
          </>
        )}
      </Box>
    </Dialog>
  )
}
